<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { rotate as rotatePage } from '@/routes/tools/pdf';
import rotateTool from '@/routes/tools/pdf/rotate';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.rotate.seo',
    fallbackTitleKey: 'tools.pdf.rotate.title',
    fallbackTitleLabel: 'Rotate PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: rotatePage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.rotate.title',
                title: 'Rotate PDF',
                href: rotatePage.url(),
            },
        ],
    },
});

type Rotation = '90' | '180' | '270';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const rotation = ref<Rotation>('90');
const status = ref<Status>('idle');
const statusMessage = ref('');
const previewPage = ref(1);

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();
const totalPreviewPages = computed(() => Math.max(1, pageCount.value));

const rotationOptions = computed(() => {
    type Opt = { label: string; value: Rotation; icon: string };

    const opts: Opt[] = [
        {
            label: t('tools.pdf.ui.rotate_90_cw', '90° Clockwise'),
            value: '90',
            icon: '↻',
        },
        {
            label: t('tools.pdf.ui.rotate_180', '180°'),
            value: '180',
            icon: '↕',
        },
        {
            label: t('tools.pdf.ui.rotate_90_ccw', '90° Counter-clockwise'),
            value: '270',
            icon: '↺',
        },
    ];

    return opts;
});

const cssDeg: Record<Rotation, number> = { '90': 90, '180': 180, '270': -90 };

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    status.value = 'idle';

    if (!file.value) {
        unload();

        return;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    await renderPreviewPage(1);
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    previewPage.value = 1;
    unload();
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 420);
}

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());

            return (
                (Object.values(parsed.errors ?? {}).flat()[0] as string) ||
                t('tools.pdf.ui.rotate_failed', 'Rotation failed.')
            );
        } catch {
            return t('tools.pdf.ui.rotate_failed', 'Rotation failed.');
        }
    }

    return t('tools.pdf.ui.rotate_failed', 'Rotation failed.');
}

async function rotate(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.rotating', 'Rotating PDF...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('rotation', rotation.value);

    try {
        const response = await axios.post(rotateTool.submit.url(), formData, {
            responseType: 'blob',
        });
        const cd = (response.headers['content-disposition'] as string) ?? '';
        const filename = cd.match(/filename="?([^"]+)"?/)?.[1] ?? 'rotated.pdf';
        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.rotated_success',
            'PDF rotated successfully!',
        );
        file.value = null;
        unload();
    } catch (err) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}

onUnmounted(unload);
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-rotate" />

    <ToolLayout
        :title="t('tools.pdf.rotate.title', 'Rotate PDF')"
        :subtitle="t('tools.pdf.rotate.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <!-- ── Left: preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="pageCount"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <div
                        class="relative z-0 flex min-h-48 items-center justify-center px-2 py-4"
                    >
                        <div
                            class="transition-transform duration-300"
                            :style="{
                                transform: `rotate(${cssDeg[rotation]}deg)`,
                            }"
                        >
                            <canvas
                                ref="pdfCanvas"
                                class="max-h-none max-w-full rounded-md bg-white shadow-md dark:bg-gray-950"
                            />
                        </div>
                    </div>
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.rotate_preview_hint',
                                    'Preview shows selected page after the chosen rotation is applied.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t('tools.pdf.ui.all_pages', 'All pages'),
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{ t('tools.pdf.ui.rotation_section', 'Rotation') }}
                    </p>
                    <div class="flex flex-col gap-2">
                        <button
                            v-for="opt in rotationOptions"
                            :key="opt.value"
                            type="button"
                            class="flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                            :class="
                                rotation === opt.value
                                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                    : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                            "
                            @click="rotation = opt.value"
                        >
                            <span class="text-xl leading-none">{{
                                opt.icon
                            }}</span>
                            <span>{{ opt.label }}</span>
                        </button>
                    </div>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="!file || status === 'loading'"
                    @click="rotate"
                >
                    {{ t('tools.pdf.ui.rotate_pdf_button', 'Rotate PDF') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
